import {
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form945DueDates = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 945 filing due date for the tax year 2025 | TaxZerone ®"
          description="The deadline for Form 945 for the tax year 2025 is February 02, 2026. File with TaxZerone-Your trusted IRS-authorized tax filing platform for filing form 945"
          keywords=""
          canonical="resources/employment-tax/form-945-due-dates/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 945 </span>
          </span>{' '}
          Due Dates
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Businesses need to file form 945 before the deadline to ensure
          compliance with IRS
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=945duedates`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-945-due-dates/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 945 Due Date at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-945-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 945
                        </Link>
                        {' ('}
                        Annual Return of Withheld Federal Income Tax )
                        {
                          ' is used by businesses to report federal income tax withheld from nonpayroll payments, such as pensions, annuities, IRA distributions, gambling winnings, and certain other payments.'
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Who Must File:</b>
                        {
                          ' Businesses or payers that withhold federal income tax from applicable nonpayroll payments.'
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Filing Frequency:</b>
                        {' Form 945 is filed '}
                        <b>annually</b>.
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Filing Deadline:</b>
                        {' For the '}
                        <b>2025 tax year</b>
                        {', Form 945 is due '}
                        <b>February 2, 2026</b>
                        {
                          '. If all required deposits were made on time and in full, the return may generally be filed by '
                        }
                        <b>February 10, 2026</b>.
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Information Required:</b>
                        {
                          ' Businesses need their business name, trade name, EIN, address, total federal income tax withheld, and applicable nonpayroll payment information.'
                        }
                      </span>
                    </li>
                  </Box>
                </Box>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box />
                  {/* <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  ></Box> */}
                </Stack>
                {/* <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 943?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#when" sx={{ color: '#0202ff' }}>
                          When to File Form 943?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#where" sx={{ color: '#0202ff' }}>
                          Where to File Form 943?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#taxes" sx={{ color: '#0202ff' }}>
                          Depositing Your Taxes
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link
                          href="#interest"
                          id="what"
                          sx={{ color: '#0202ff' }}
                        >
                          Penalties and Interest?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Form 945?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  {' '}
                  <Link
                    href="/resources/employment-tax/form-945-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 945
                  </Link>{' '}
                  reports the total nonpayroll payments made by the business and
                  the total tax held from the payment. It is the annual tax
                  return form to be filed by the employer.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important dates in filing form 945
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Filing Form 945 on time is important for businesses to avoid
                  penalties and comply with IRS regulations.
                </Typography>
                <Box className="deadline-dates" sx={{}}>
                  <Typography
                    variant="body1"
                    sx={{ p: 2, color: '#fff', fontSize: 18, fontWeight: 500 }}
                  >
                    <b style={{ fontSize: 22 }}>
                      {' '}
                      Deadline for the Tax year 2025 :
                    </b>{' '}
                    February 02, 2026
                  </Typography>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, mt: 3, fontSize: 14.5 }}
                >
                  <b>Note:</b> If deposits were made on time and in full for the
                  year, the IRS gives an additional 10 days to file form 945.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What are the details required?
                </Typography>
                <Typography variant="body1">
                  Form 945 requires details:
                </Typography>

                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Business name, Trade name, EIN, address of the business
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Tax withheld from the nonpayroll payment
                  </li>
                  <li style={{ marginBottom: 12 }}>Back up amount withheld</li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Nonpayroll payments include pensions (including distributions
                  from tax-favored retirement plans), annuities, IRA
                  distributions, military retirement, Indian gaming profits, and
                  gambling winnings.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Filing Method:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b>E-Filing:</b> Form 945 can be easily filed using the
                    electronic filing method. It is a Quick, easy, and reliable
                    filing Method.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Paper filing:</b>The IRS allows businesses to file Form
                    945 using the paper filing method. Filer needs to complete
                    the form and mail it to the addresses mentioned by the IRS
                    based on the location.
                  </li>
                </ul>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent={{ xs: 'center', md: 'space-between' }}
                  alignItems="center"
                  spacing={2}
                  sx={{
                    py: 2,
                    px: { xs: 1, md: 2 },
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    border: '1px solid #ccc',
                    mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      flexGrow: 1,
                      textAlign: { xs: 'center', md: 'left' },
                      width: '100%',
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '19px !important',
                        fontWeight: 600,
                        mb: 1,
                        lineHeight: 1.5,
                      }}
                    >
                      TaxZerone suggests employers use the E-Filing method for
                      easy filing and faster processing of Form 945.
                    </Typography>
                  </Box>
                  <Box sx={{ width: 180 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=945duedates`}
                    >
                      E-File Now
                    </Button>
                  </Box>
                </Stack>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Penalties for late deposit and late filing
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mt: 1,
                    mb: 1,
                  }}
                >
                  1. Failure-to-File (FTF) Penalty
                </Typography>
                <Typography variant="body1">
                  A penalty for not filing a tax return on time.
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>How it works:</li>
                  <ul>
                    {' '}
                    <li style={{ marginBottom: 12 }}>
                      The penalty is 5% of the unpaid tax for each month (or
                      part of a month) the return is late.
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      The maximum penalty is 25% of the unpaid tax.
                    </li>
                  </ul>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mt: 1,
                    mb: 1,
                  }}
                >
                  2. Failure-to-Pay (FTP) Penalty
                </Typography>
                <Typography variant="body1">
                  A penalty for not paying the tax owed on time.
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>How it works:</li>
                  <ul>
                    {' '}
                    <li style={{ marginBottom: 12 }}>
                      The penalty is 0.5% of the unpaid tax for each month (or
                      part of a month) the payment is late.
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      The maximum penalty is 25% of the unpaid tax.
                    </li>
                  </ul>
                </ul>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>Note:</b> The IRS will also charge interest on the
                  penalties till it is paid
                </Alert>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0, lineHeight: 1.3 }}
                      >
                        TaxZerone offers a reliable E-Filing solution for Form
                        945
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 130, textAlign: 'center' }}
                      id="common"
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=945duedates`}
                      >
                        File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      {/* <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file Form 940 within a few minutes using TaxZerone
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Choose a simplified tax filing process. Choose TaxZerone.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=940duedates`}
              >
                E-file Form 940
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container> */}
    </AppLayout>
  );
};

export default Form945DueDates;
